import * as ScrollAreaPrimitive from '@radix-ui/react-scroll-area';
import * as React from 'react';

import { cn } from '@apps/web/lib/utils';

type ScrollAreaProps = React.ComponentPropsWithoutRef<
    typeof ScrollAreaPrimitive.Root
> & {
    readonly forceMountScrollbar?: boolean;
    readonly scrollbarClassName?: string;
    readonly viewportClassName?: string;
};

const ScrollArea = React.forwardRef<
    React.ElementRef<typeof ScrollAreaPrimitive.Root>,
    ScrollAreaProps
>(function ScrollArea(
    {
        className,
        children,
        forceMountScrollbar = false,
        scrollbarClassName,
        viewportClassName,
        ...props
    },
    ref,
) {
    return (
        <ScrollAreaPrimitive.Root
            ref={ref}
            data-slot="scroll-area"
            className={cn('relative overflow-hidden', className)}
            {...props}
        >
            <ScrollAreaPrimitive.Viewport
                data-slot="scroll-area-viewport"
                // `size-full` sizes the viewport with `height: 100%`, which
                // only resolves when every ancestor height is definite. Under
                // an ancestor whose height is content-sized and clamped by
                // `max-height` (e.g. the admin agent dock shell), percentages
                // resolve to `auto`, the viewport grows to its content height,
                // and the scroll region silently stops scrolling. `min-h-0
                // flex-1` is inert in the default block layout but lets a
                // caller opt into flex sizing — which resolves through
                // indefinite chains — by passing `flex flex-col` on the Root.
                className={cn(
                    'focus-visible:ring-ring/50 size-full min-h-0 flex-1 rounded-[inherit] transition-[color,box-shadow] outline-none focus-visible:ring-[3px] focus-visible:outline-1',
                    viewportClassName,
                )}
            >
                {children}
            </ScrollAreaPrimitive.Viewport>
            <ScrollBar
                {...(forceMountScrollbar ? { forceMount: true } : {})}
                className={scrollbarClassName}
            />
            <ScrollAreaPrimitive.Corner />
        </ScrollAreaPrimitive.Root>
    );
});

ScrollArea.displayName = ScrollAreaPrimitive.Root.displayName ?? 'ScrollArea';

function ScrollBar({
    className,
    orientation = 'vertical',
    ...props
}: React.ComponentProps<typeof ScrollAreaPrimitive.ScrollAreaScrollbar>) {
    return (
        <ScrollAreaPrimitive.ScrollAreaScrollbar
            data-slot="scroll-area-scrollbar"
            orientation={orientation}
            className={cn(
                'flex touch-none p-px transition-colors select-none',
                orientation === 'vertical' &&
                    'h-full w-2.5 border-l border-l-transparent',
                orientation === 'horizontal' &&
                    'h-2.5 flex-col border-t border-t-transparent',
                className,
            )}
            {...props}
        >
            <ScrollAreaPrimitive.ScrollAreaThumb
                data-slot="scroll-area-thumb"
                className="bg-border relative flex-1 rounded-full"
            />
        </ScrollAreaPrimitive.ScrollAreaScrollbar>
    );
}

export { ScrollArea, ScrollBar };
